///
/// Copyright (c) 2017-present, Siklu Ltd.
/// All rights reserved.
///

import { Injectable, NgZone } from '@angular/core';
import { GtthLogicalTopology } from '../entities/logical-topology';
import { GtthCreateLinkCommand } from './commands/link/create-link-command';
import { GtthToolbarModeChangedCommand } from './commands/toolbar/toolbar-mode-changed-command';
import { GtthSquareSelectionService } from './square-selection-service';
import { GtthNotificationsService } from './notifications-service';
import { GtthProjectInfoViewService } from '../../../layouts/navbar/project-info-view.service';
import { GtthAbstractCommand } from './abstract-command';
import { ActivityDTO } from '../../../services/project-socket/activity-dto.model';
import { GtthActivateDesignIterationCommand } from './commands/design/activate-design-iteration-command';
import { GtthAutoConnectCommand } from './commands/design/auto-connect-command';
import { GtthAutoDesignCommand } from './commands/design/auto-design-command';
import { GtthModifyLinkInfoCommand } from './commands/link/modify-link-info-command';
import { GtthRemoveLinkCommand } from './commands/link/remove-link-command';
import { GtthCreateTerminalCommand } from './commands/node/create-terminal-command';
import { GtthModifyUnitInfoCommand } from './commands/node/modify-unit-info-command';
import { GtthMoveUnitCommand } from './commands/node/move-unit-command';
import { GtthRemoveTerminalCommand } from './commands/node/remove-terminal-command';
import { GtthImportTopologyCommand } from './commands/project/import-topology-command';
import { GtthUpdateMainConfigCommand } from './commands/project/update-main-config-command';
import { ProjectWindowsEvents } from '../../project-windows/project-windows-events.model';
import { GtthPreloaderModalEvent } from '../../../shared/preloader-modal/preloader-modal-event';
import { GtthBomReportService } from '../../project-windows/bom_report/bom-report.service';
import { StateStorageService } from '../../../shared/auth/state-storage.service';
import { Observable, Subject, Subscription } from 'rxjs';
import { GtthOpenProjectCommand } from './commands/project/open-project-command';
import { GtthRefreshTopologyCommand } from './commands/project/refresh-topology-command';
import { GtthLockProjectCommand } from './commands/project/lock-project-command';
import { GtthUnlockProjectCommand } from './commands/project/unlock-project-command';
import { GtthRemoveMultipleEntitiesCommand } from './commands/remove-multiple-entities-command';
import { GtthCreateMultipleEntitiesCommand } from './commands/create-multiple-entities-command';
import { GtthEnableMultipleEntitiesCommand } from './commands/enable-multiple-entities-command';
import { GtthDisableMultipleEntitiesCommand } from './commands/disable-multiple-entities-command';
import { GtthChangeTypeForSelectedLiksCommand } from './commands/link/change-type-for-selected-links-command';
import { GtthCreateLosesToSelectedEntitiesCommand } from './commands/link/create-loses-between-selected-entities-command';
import { GtthCreateLosesToStationNeighborsCommand } from './commands/link/create-loses-to-station-neighbors-command';
import { GtthVerifyLosesSelectedEntitiesCommand } from './commands/link/verify-loses-for-selected-entities-command';
import { GtthVerifyLosesToStationNeighborsCommand } from './commands/link/verify-loses-to-Station-neighbors-command';
import { GtthFreezeSelectedLinksCommand } from './commands/link/freeze-selected-links-command';
import { NodeFeatureView } from '../entities/node/view/node-feature-view';
import { GtthEventManager } from '../../../services/event-manager.service';
import { GtthHouseDetectionCommand } from './commands/node/house-detection-command';
import { GtthMultipleAutoDesignCommand } from './commands/design/multiple-auto-desing-command';
import { GtthProgressWindowService } from '../../project-windows/progress-window/progress-window.service';
import { GtthProgressWindowModel } from '../../project-windows/progress-window/progress-window.model';
import { GtthRequestLockProjectCommand } from './commands/project/request-lock-project-command';
import { GtthLogSenderService } from '../../../services/logger/backend-logging/logs-sender.service';
import { GtthNodeFeatureDtoModel } from '../../../services/project-socket/dto/gtth-node-feature-dto.model';
import { GtthLinkFeatureDtoModel } from '../../../services/project-socket/dto/gtth-link-feature-dto.model';
import { GtthChangeSectorAngleCommand } from './commands/link/change-sector-angle-command';
import { ProjectProductsService } from './project-products-service';
import { GtthSetProjectBomCommand } from './commands/bom/set-project-bom-command';
import { GtthGetProjectBomCommand } from './commands/bom/get-project-bom-command';
import { ActionsToolbarModeEnum, GtthStepGroupEnum, NodeConnectedDataDTO, ProjectDTO } from '../../../generated/shared-types.model';
import { ProjectInteractiveWindowsPopupService } from '../../project-windows/project-interactive-windows-popup.service';
import { SOCKET_LOGS_ENABLED } from '../../../../environments/environment';
import { SocketProjectService } from '../../../services/project-socket/project-socket.service';
import { GtthInfoWindowsService } from '../map-entities-info/info-windows-service';
import { GtthChangeMHTypeMultipleUnitsCommand } from './commands/node/change-mh-type-multiple-units-command';
import { GtthProjectStateService } from './project-state.service';
import { GtthModifyNodesAttributes } from './commands/modify-multiple-entities-command';
import { GtthApplyUserSessionCommand } from './commands/session/apply-user-session-command';
import { GtthProjectUserSessionService } from './project-user-session.service';
import { GtthApplyViewStateCommand } from './commands/session/apply-view-state-command';
import { PolygonsService } from '../entities/polygon/polygons-service';
import { Router } from '@angular/router';
import { GtthReportsService } from '../../../services/project/reports.service';
import { GtthActivateLayerCommand } from './commands/toolbar/activate-layer-command';
import { GtthClearDesignCommand } from './commands/design/manual-design/clear-design-command';
import { GtthAutoSelectProductsCommand } from './commands/design/manual-design/auto-select-products-command';
import { GtthDesignRingsCommand } from './commands/design/manual-design/design-rings-command';
import { GtthChangeLayerMultipleUnitsCommand } from './commands/node/change-layer-multiple-units-command';
import { GtthAutoSetFrequenciesCommand } from './commands/design/manual-design/auto-set-frequencies-command';
import { GtthAutoConfigureRingsCommand } from './commands/design/manual-design/auto-configure-rings-command';
import { GtthDesignPtMPCommand } from './commands/design/manual-design/design-ptmp-command';
import { GtthChangeBlockedByPoleMultipleUnitsCommand } from './commands/node/change-blocked-by-pole-multiple-units-command';
import { Principal } from '../../../shared/auth/principal.service';
import { GtthRequestLockFromAnotherUserCommand } from './commands/project/request-lock-from-another-user-command';
import { GtthConfirmationService } from './confirm-notification.service';
import { GtthChangeLinkNodesDataCommand } from './commands/link/change-link-nodes-data-command';
import { GtthChangeDeviceAzimuthMultipleUnitsCommand } from './commands/node/change-device-azimuth-multiple-units-command';
import { GtthChangeFrequenciesMultipleLinksCommand } from './commands/link/change-frequencies-multiple-links-command';
import { isDefined } from '@siklu/sui-lib/utils';
import { SearchMarkerView } from '../entities/marker/search/search-marker-view';

@Injectable({
    providedIn: 'root'
})
export class GtthHistoryService {
    map: google.maps.Map;
    public selectedStepNumber: GtthStepGroupEnum;
    public toolbarMode: ActionsToolbarModeEnum;
    isEditModeEnabled = false;
    editModeEnabledChangedSubject: Subject<boolean> = new Subject<boolean>();
    isCtrlKeyPressed = false;
    logicalTopology: GtthLogicalTopology;
    currentMarkerIndex: number;
    currentIndex: number;
    historyArray: GtthAbstractCommand[];
    public canCommandLockProject = false;
    public ignoreMoveCommandForId: number;
    public autoConfigRingsErrorNodes: NodeConnectedDataDTO[];
    public topologyChanged: boolean;
    private readonly commandsMap: Map<string, new () => GtthAbstractCommand>;
    private commandsCounter: number;
    private readonly currentCommands: Map<number, GtthAbstractCommand>;
    private readonly currentCommandsState: Subject<boolean>;
    private draggableNodeViewObject: NodeFeatureView;
    private trackerServiceSubscription: Subscription;
    private searchMarker: SearchMarkerView;

    constructor(
        private readonly trackerService: SocketProjectService,
        public infoWindowsService: GtthInfoWindowsService,
        public selectAreaService: GtthSquareSelectionService,
        public projectInfoViewService: GtthProjectInfoViewService,
        public bomReportService: GtthBomReportService,
        public sessionStorage: StateStorageService,
        public ngZone: NgZone,
        public progressWindowService: GtthProgressWindowService,
        public projectProductsService: ProjectProductsService,
        public interactiveWindowsService: ProjectInteractiveWindowsPopupService,
        public eventManager: GtthEventManager,
        public notificationService: GtthNotificationsService,
        public confirmationService: GtthConfirmationService,
        public projectStateService: GtthProjectStateService,
        public projectUserSessionService: GtthProjectUserSessionService,
        public polygonsService: PolygonsService,
        public router: Router,
        public reportsService: GtthReportsService,
        public principal: Principal
    ) {
        this.clear();
        this.commandsMap = new Map();
        this.initCommandsMap();

        this.currentCommandsState = new Subject();

        this.bomReportService.historyService = this;
        this.infoWindowsService.historyService = this;
        this.progressWindowService.historyService = this;
        this.selectAreaService.historyService = this;
        this.currentCommands = new Map<number, GtthAbstractCommand>();

        // do not close subscription object because this subscription will live all the time till browser close/refresh
        this.projectInfoViewService.layerChangedSubscription().subscribe((activeLayerIdx) => {
            this.doCommandBypassHistory(this.command(GtthActivateLayerCommand).create(activeLayerIdx));
        });
    }

    public getCurrentCommandsStateSubscription(): Observable<boolean> {
        return this.currentCommandsState.asObservable();
    }

    public mapFitBounds(bounds) {
        if (this.map) {
            this.ngZone.runOutsideAngular(() => {
                this.map.fitBounds(bounds);
            });
        }
    }

    public mapSetCenterAndZoom(latitude: number, lontitude: number, zoomLevel?: number) {
        if (this.map) {
            this.ngZone.runOutsideAngular(() => {
                this.map.setCenter(new google.maps.LatLng(latitude, lontitude));
                if (zoomLevel) {
                    this.map.setZoom(zoomLevel);
                }
            });
        }
    }

    addSearchMarker(coordinates: google.maps.LatLngLiteral, title: string) {
        this.clearSearchMarker();
        this.searchMarker = new SearchMarkerView(coordinates, title);
        this.searchMarker.draw(this.map);
    }

    clearSearchMarker() {
        if (this.searchMarker) {
            this.searchMarker.hide();
            this.searchMarker = undefined;
        }
    }

    hasSearchMarkers(): boolean {
        return !!this.searchMarker;
    }

    async openProject(project: ProjectDTO, logSender: GtthLogSenderService, userLogin: string): Promise<boolean> {
        this.canCommandLockProject = true;

        this.projectStateService.setProject(project);

        if (!project.id) {
            throw new Error('Project not found');
        }

        this.trackerService.connect();
        await this.trackerService.subscribe(project.id);

        this.trackerServiceSubscription = this.trackerService.receive().subscribe((activity: ActivityDTO) => {
            this.receivedActivity(activity);
        });

        this.logicalTopology = new GtthLogicalTopology();
        this.topologyChanged = false;

        const oldSessionId = this.sessionStorage.getProjectSessionId(project.id);
        const openCommand = this.command(GtthOpenProjectCommand).create(oldSessionId);

        const projectOpenedCorrectly = await this.doCommandBypassHistory(openCommand);

        if (projectOpenedCorrectly) {
            this.sessionStorage.setProjectSessionId(project.id, openCommand.getNewSessionId());
            await this.projectProductsService.loadProducts(project.id, true);
            const getBomCommand = this.command(GtthGetProjectBomCommand);
            await this.doCommandBypassHistory(getBomCommand);
            this.bomReportService.setBom(getBomCommand.bomConfig);

            const lockProjectCommand = this.command(GtthLockProjectCommand).create(false);
            await this.doCommandBypassHistory(lockProjectCommand);

            await this.doCommandBypassHistory(this.command(GtthApplyUserSessionCommand));

            const refreshProject = this.command(GtthRefreshTopologyCommand).create(true);

            const refreshResult = await this.doCommandBypassHistory(refreshProject);

            await this.doCommandBypassHistory(this.command(GtthApplyViewStateCommand).create(project.lastEditUserId === userLogin));

            this.projectUserSessionService.openForModification();

            this.projectInfoViewService.setProjectName(project.name);
            if (this.logicalTopology.layersModeEnabled) {
                this.projectInfoViewService.showLayers(this.logicalTopology.layers, this.logicalTopology.activeLayerIdx);
            } else {
                this.projectInfoViewService.hideLayers();
            }
            this.projectInfoViewService.showInfo();

            return refreshResult;
        } else {
            this.sessionStorage.clearProjectSessionId(project.id);
            if (this.trackerServiceSubscription) {
                this.trackerServiceSubscription.unsubscribe();
                this.trackerServiceSubscription = null;
            }
            this.trackerService.unsubscribe();
            this.trackerService.disconnect();
            throw Error('Open project exception');
        }
    }

    async closeProject(): Promise<void> {
        this.clearSelectionCloseWindowAndArea();
        if (this.draggableNodeViewObject) {
            this.draggableNodeViewObject.changeDraggable(false);
            this.draggableNodeViewObject = null;
        }

        this.eventManager.broadcast({ name: ProjectWindowsEvents.CLOSE_MODAL_WINDOW, content: { force: true } });

        if (this.projectInfoViewService) {
            this.projectInfoViewService.hideInfo();
        }

        if (this?.trackerService?.isConnected) {
            await this.doCommandBypassHistory(this.command(GtthUnlockProjectCommand));

            // GtthUnlockProjectCommand opens and closes it's own mask so now we need to recreate it
            this.eventManager.broadcast({
                name: 'togglePreloaderModal',
                eventObject: GtthPreloaderModalEvent.show('Loading')
            });

            this.clear();

            this.logicalTopology.clearTopologyEntities();
            this.logicalTopology.completeDesignStateSubject();
            this.polygonsService.hidePolygons();

            this.map = undefined;

            this.logicalTopology = null;
            this.projectProductsService.clearData();

            this.sessionStorage.clearProjectSessionId(this.projectStateService.getProjectId());

            if (this.trackerServiceSubscription) {
                this.trackerServiceSubscription.unsubscribe();
                this.trackerServiceSubscription = null;
            }

            this.trackerService.unsubscribe();
            this.trackerService.disconnect();

            this.projectStateService.clearProject();
            this.projectUserSessionService.clear();

            this.eventManager.broadcast({
                name: 'togglePreloaderModal',
                eventObject: GtthPreloaderModalEvent.hide()
            });
        } else {
            this.eventManager.broadcast({
                name: 'togglePreloaderModal',
                eventObject: GtthPreloaderModalEvent.hide()
            });
        }
    }

    sendCommandActivity(activity: ActivityDTO) {
        if (this?.trackerService?.isConnected) {
            if (SOCKET_LOGS_ENABLED) {
                console.log('Send activity', activity);
            }
            this.trackerService.sendActivity(this.projectStateService.getProjectId(), activity);
        } else {
            activity.successful = false;
            this.receivedActivity(activity);
        }
    }

    modeChanged(data: { type: ActionsToolbarModeEnum; terminalLayer?: number }) {
        if (data.type === this.toolbarMode && (!isDefined(data.terminalLayer) || data.terminalLayer === this.currentMarkerIndex)) {
            data.type = ActionsToolbarModeEnum.POINTER;
            this.currentMarkerIndex = -1;
        } else if (isDefined(data.terminalLayer)) {
            this.currentMarkerIndex = data.terminalLayer;
        }

        this.doCommandBypassHistory(this.command(GtthToolbarModeChangedCommand).create(data.type));
    }

    clearHistory() {
        this.historyArray = [];
        this.currentIndex = -1;
        this.commandsCounter = 0;
    }

    clear() {
        if (this.currentCommands) {
            this.deleteCommandFromMap();
        }
        this.commandsCounter = 0;
        this.clearHistory();
    }

    doCommandBypassHistory(command: GtthAbstractCommand, doLocally = false): Promise<boolean> {
        if (!doLocally) {
            this.addCommandToMap(command);
        }

        return command.do(doLocally).then(async (result: boolean) => {
            if (!doLocally) {
                this.deleteCommandFromMap(command.getCommandId());
            }

            if (command.isClearDesign() && !doLocally && this.logicalTopology.isShowPlanningMode) {
                this.bomReportService.clearBom();
                this.logicalTopology.clearNodeNamesSectorsToShow();
                this.logicalTopology.clearDesignIterationInfo();
                await this.logicalTopology.setShowPlanningMode(this, false, true);
            }

            return result;
        });
    }

    undoCommandBypassHistory(command: GtthAbstractCommand, doLocally = false): Promise<boolean> {
        if (!doLocally) {
            this.addCommandToMap(command);
        }
        return command.undo(doLocally).then((result: boolean) => {
            if (!doLocally) {
                this.deleteCommandFromMap(command.getCommandId());
            }
            return result;
        });
    }

    doCommand(command: GtthAbstractCommand): Promise<boolean> {
        const doLocally = false;

        if (!this.isEditModeEnabled && command.isDisabledInReadOnlyMode()) {
            return Promise.resolve(false);
        }

        this.addCommandToMap(command);

        return command.do(doLocally).then(async (isSuccessful: boolean) => {
            this.deleteCommandFromMap(command.getCommandId());

            if (command.isModifyTopology()) {
                this.topologyChanged = true;
            }

            if (isSuccessful) {
                if (this.currentIndex < this.historyArray.length - 1) {
                    this.historyArray.length = this.currentIndex + 1;
                }
                if (command.isModifyTopology()) {
                    this.currentIndex = this.historyArray.push(command) - 1;
                }
            }

            if (command.isClearDesign() && !doLocally && this.logicalTopology.isShowPlanningMode) {
                this.bomReportService.clearBom();
                this.logicalTopology.clearNodeNamesSectorsToShow();
                this.logicalTopology.clearDesignIterationInfo();
                await this.logicalTopology.setShowPlanningMode(this, false, true);
            }

            return Promise.resolve(isSuccessful);
        });
    }

    undoCommand(): Promise<boolean> {
        const doLocally = false;
        if (this.currentIndex > -1) {
            const command = this.historyArray[this.currentIndex];
            this.currentIndex--;
            this.addCommandToMap(command);
            return command.undo(doLocally).then((result: boolean) => {
                this.deleteCommandFromMap(command.getCommandId());
                return result;
            });
        } else {
            return Promise.resolve(false);
        }
    }

    redoCommand(): Promise<boolean> {
        const doLocally = false;
        if (this.currentIndex < this.historyArray.length - 1) {
            this.currentIndex++;
            const command = this.historyArray[this.currentIndex];
            this.addCommandToMap(command);
            return command.do(doLocally).then((result: boolean) => {
                this.deleteCommandFromMap(command.getCommandId());
                return result;
            });
        } else {
            return Promise.resolve(false);
        }
    }

    isUndoEnabled(): boolean {
        return this.currentIndex >= 0;
    }

    isRedoEnabled(): boolean {
        return this.currentIndex < this.historyArray.length - 1;
    }

    ignoreMoveCommands(stationId: number) {
        this.ignoreMoveCommandForId = stationId;
    }

    proceedMoveCommands(_stationId: number) {
        this.ignoreMoveCommandForId = undefined;
    }

    triggerNodeAbilityToDrag(nodeViewObject: NodeFeatureView) {
        if (this.draggableNodeViewObject === nodeViewObject) {
            return;
        }

        if (this.draggableNodeViewObject) {
            this.draggableNodeViewObject.changeDraggable(false);
            this.draggableNodeViewObject = undefined;
        }

        if (nodeViewObject) {
            this.draggableNodeViewObject = nodeViewObject;
            this.draggableNodeViewObject.changeDraggable(true);
        }
    }

    getInfoWindowEntityId(): string | number {
        return this.infoWindowsService.isWindowOpen ? this.infoWindowsService.getInfoWindowEntityId() : undefined;
    }

    closeInfoWindows() {
        if (this.infoWindowsService.isWindowOpen) {
            this.infoWindowsService.closeInfoWindow();
        }
    }

    clearSelectArea(hideInfoWindow = true) {
        if (this.selectAreaService.overlay) {
            this.selectAreaService.destroyOverlay(hideInfoWindow);
        }
    }

    clearSelectionCloseWindowAndArea() {
        this.closeInfoWindows();
        if (this.logicalTopology) {
            this.logicalTopology.clearSelectedSectors();
            this.clearSelectArea();
        }

        if (this?.logicalTopology?.hasSelectedEntities()) {
            this.logicalTopology.clearSelection();
        }

        this.doCommandBypassHistory(this.command(GtthToolbarModeChangedCommand).create(ActionsToolbarModeEnum.POINTER));
    }

    closeInfoWindowAndArea() {
        this.closeInfoWindows();
        this.clearSelectArea();
        this.logicalTopology.clearSelectedSectors();
    }

    selectWholeTopologyLayer() {
        if (this.logicalTopology.layersModeEnabled) {
            this.projectInfoViewService.showLayers(this.logicalTopology.layers, GtthLogicalTopology.WHOLE_TOPOLOGY_LAYER_INDEX);
        }
    }

    resolveRequestLockCommand(unlockedUser: string): void {
        this.currentCommands.forEach((abstractCommand) => {
            // if another user unlocked a project and current user requested the lock - 'release' request command
            if (abstractCommand.getCommandName() === 'GtthRequestLockFromAnotherUserCommand') {
                const requestLockCommand = abstractCommand as GtthRequestLockFromAnotherUserCommand;
                if (requestLockCommand.currentlyLockedBy === unlockedUser) {
                    requestLockCommand.handleUserDecision(true);
                }
            }
        });
    }

    command<T extends GtthAbstractCommand>(c: new () => T): T {
        return new c().setHistoryService(this).setProject(this.projectStateService.getProject()).setCommandId(this.commandsCounter++);
    }

    private receivedActivity(activity: ActivityDTO) {
        if (activity.nodes) {
            activity.nodes = activity.nodes.map((value) => GtthNodeFeatureDtoModel.fromData(value));
        }
        if (activity.links) {
            activity.links = activity.links.map((value) => GtthLinkFeatureDtoModel.fromData(value));
        }

        const mapHasElement =
            this.currentCommands.has(activity.commandId) &&
            this.currentCommands.get(activity.commandId).getCommandName() === activity.commandName &&
            activity.userLogin === this.principal.getLoginOrUndefined();

        if ((!activity.uiBroadcast || activity.commandName !== 'GtthMoveUnitCommand') && mapHasElement) {
            const command = this.currentCommands.get(activity.commandId);
            command.applyActivity(activity);
        } else {
            const command = this.commandByName(activity.commandName);
            this.commandsCounter--;
            if (command) {
                command.applyActivity(activity);
            } else if (activity.commandName === 'GtthAbortDesignCommand') {
                this.canCommandLockProject = false;
                this.progressWindowService.show(GtthProgressWindowModel.createFromActivityDTO(activity));
            }
        }
    }

    private addCommandToMap(command) {
        this.currentCommands.set(command.getCommandId(), command);
        this.currentCommandsState.next(true);
    }

    private deleteCommandFromMap(commandId?: number) {
        if (!commandId && commandId !== 0) {
            this.currentCommands.clear();
        } else {
            this.currentCommands.delete(commandId);
        }
        if (!this.currentCommands || this.currentCommands.size === 0) {
            this.currentCommandsState.next(false);
        }
    }

    private initCommandsMap(): void {
        this.commandsMap.set('GtthActivateDesignIterationCommand', GtthActivateDesignIterationCommand);
        this.commandsMap.set('GtthAutoConnectCommand', GtthAutoConnectCommand);
        this.commandsMap.set('GtthCreateLinkCommand', GtthCreateLinkCommand);
        this.commandsMap.set('GtthModifyLinkInfoCommand', GtthModifyLinkInfoCommand);
        this.commandsMap.set('GtthRemoveLinkCommand', GtthRemoveLinkCommand);
        this.commandsMap.set('GtthCreateTerminalCommand', GtthCreateTerminalCommand);
        this.commandsMap.set('GtthModifyUnitInfoCommand', GtthModifyUnitInfoCommand);
        this.commandsMap.set('GtthMoveUnitCommand', GtthMoveUnitCommand);
        this.commandsMap.set('GtthRemoveTerminalCommand', GtthRemoveTerminalCommand);
        this.commandsMap.set('GtthImportTopologyCommand', GtthImportTopologyCommand);
        this.commandsMap.set('GtthUpdateMainConfigCommand', GtthUpdateMainConfigCommand);
        this.commandsMap.set('GtthRemoveMultipleEntitiesCommand', GtthRemoveMultipleEntitiesCommand);
        this.commandsMap.set('GtthCreateMultipleEntitiesCommand', GtthCreateMultipleEntitiesCommand);
        this.commandsMap.set('GtthEnableMultipleEntitiesCommand', GtthEnableMultipleEntitiesCommand);
        this.commandsMap.set('GtthDisableMultipleEntitiesCommand', GtthDisableMultipleEntitiesCommand);
        this.commandsMap.set('GtthChangeTypeForSelectedLiksCommand', GtthChangeTypeForSelectedLiksCommand);
        this.commandsMap.set('GtthCreateLosesToSelectedEntitiesCommand', GtthChangeFrequenciesMultipleLinksCommand);
        this.commandsMap.set('GtthChangeFrequenciesMultipleLinksCommand', GtthCreateLosesToSelectedEntitiesCommand);
        this.commandsMap.set('GtthCreateLosesToStationNeighborsCommand', GtthCreateLosesToStationNeighborsCommand);
        this.commandsMap.set('GtthFreezeSelectedLinksCommand', GtthFreezeSelectedLinksCommand);
        this.commandsMap.set('GtthVerifyLosesSelectedEntitiesCommand', GtthVerifyLosesSelectedEntitiesCommand);
        this.commandsMap.set('GtthVerifyLosesToStationNeighborsCommand', GtthVerifyLosesToStationNeighborsCommand);
        this.commandsMap.set('GtthHouseDetectionCommand', GtthHouseDetectionCommand);
        this.commandsMap.set('GtthAutoDesignCommand', GtthAutoDesignCommand);
        this.commandsMap.set('GtthMultipleAutoDesignCommand', GtthMultipleAutoDesignCommand);
        this.commandsMap.set('GtthClearDesignCommand', GtthClearDesignCommand);
        this.commandsMap.set('GtthAutoSelectProductsCommand', GtthAutoSelectProductsCommand);
        this.commandsMap.set('GtthRequestLockProjectCommand', GtthRequestLockProjectCommand);
        this.commandsMap.set('GtthChangeSectorAngleCommand', GtthChangeSectorAngleCommand);
        this.commandsMap.set('GtthSetProjectBomCommand', GtthSetProjectBomCommand);
        this.commandsMap.set('GtthDesignPtMPCommand', GtthDesignPtMPCommand);
        this.commandsMap.set('GtthDesignRingsCommand', GtthDesignRingsCommand);
        this.commandsMap.set('GtthAutoSetFrequenciesCommand', GtthAutoSetFrequenciesCommand);
        this.commandsMap.set('GtthAutoConfigureRingsCommand', GtthAutoConfigureRingsCommand);
        this.commandsMap.set('GtthChangeMHTypeMultipleUnitsCommand', GtthChangeMHTypeMultipleUnitsCommand);
        this.commandsMap.set('GtthModifyNodesAttributes', GtthModifyNodesAttributes);
        this.commandsMap.set('GtthChangeLayerMultipleUnitsCommand', GtthChangeLayerMultipleUnitsCommand);
        this.commandsMap.set('GtthChangeBlockedByPoleMultipleUnitsCommand', GtthChangeBlockedByPoleMultipleUnitsCommand);
        this.commandsMap.set('GtthChangeDeviceAzimuthMultipleUnitsCommand', GtthChangeDeviceAzimuthMultipleUnitsCommand);
        this.commandsMap.set('GtthLockProjectCommand', GtthLockProjectCommand);
        this.commandsMap.set('GtthUnlockProjectCommand', GtthUnlockProjectCommand);
        this.commandsMap.set('GtthRequestLockFromAnotherUserCommand', GtthRequestLockFromAnotherUserCommand);
        this.commandsMap.set('GtthChangeLinkNodesDataCommand', GtthChangeLinkNodesDataCommand);
    }

    private commandByName(name: string): GtthAbstractCommand {
        return this.commandsMap.has(name) ? this.command(this.commandsMap.get(name)) : undefined;
    }
}
